import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

import {
    ArrowBack,
    Edit,
    Print,
} from "@mui/icons-material";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { generateHTML } from "@tiptap/html";

import StarterKit from "@tiptap/starter-kit";

import Underline from "@tiptap/extension-underline";

import Superscript from "@tiptap/extension-superscript";

import Subscript from "@tiptap/extension-subscript";

import Image from "@tiptap/extension-image";

import TextAlign from "@tiptap/extension-text-align";

import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";

import Mathematics from "@tiptap/extension-mathematics";

// import "katex/dist/katex.min.css";

// import "./questionPaper.css";
import AuthService from "../../Auth/AuthService";


const QuestionPaperView = ({
    schoolName = localStorage.getItem("name"),
    schoolLogo = localStorage.getItem("logoUrl"),
}) => {

    const { id } = useParams();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const [paper, setPaper] = useState(null);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");


    // =========================================================
    // TIPTAP EXTENSIONS
    // =========================================================

    const extensions = useMemo(
        () => [
            StarterKit,

            Underline,

            Superscript,

            Subscript,

            Image,

            TextAlign.configure({
                types: [
                    "heading",
                    "paragraph",
                ],
            }),

            Table.configure({
                resizable: false,
            }),

            TableRow,

            TableHeader,

            TableCell,

            Mathematics.configure({
                katexOptions: {
                    throwOnError: false,
                },
            }),
        ],
        []
    );


    // =========================================================
    // LOAD PAPER
    // =========================================================

    useEffect(() => {

        if (!id) {
            return;
        }

        const loadPaper = async () => {

            try {

                setLoading(true);
                setError("");

                const response = await AuthService.getAuth(AuthService.pathUrl("GET", `/question-papers/${id}`));
                console.log(response);

                setPaper(response);

            } catch (err) {
                console.error("Error loading question paper:", err);
                setError(err.response?.message || "Unable to load question paper.");
            } finally {
                setLoading(false);
            }
        };

        loadPaper();

    }, [id]);


    // =========================================================
    // JSON → HTML
    // =========================================================

    const renderContent = (content) => {
        if (!content) {
            return "";
        }

        try {

            let jsonContent = content;
            if (typeof content === "string") {
                jsonContent = JSON.parse(content);
            }

            return generateHTML(jsonContent, extensions);

        } catch (err) {
            console.error("Tiptap HTML conversion error:", err);
            return `<p>
                    Unable to display question content.
                </p>`;
        }

    };


    // =========================================================
    // TOTAL QUESTIONS
    // =========================================================

    const totalQuestions = useMemo(() => {

        if (!paper?.sections) {
            return 0;
        }

        return paper.sections.reduce(
            (total, section) =>
                total +
                (section.questions?.length || 0),
            0
        );

    }, [paper]);


    // =========================================================
    // PRINT
    // =========================================================

    const handlePrint = () => {
        window.print();
    };


    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (
            <Box
                sx={{
                    minHeight: "50vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >

                <CircularProgress />

            </Box>
        );

    }


    // =========================================================
    // ERROR
    // =========================================================

    if (error) {

        return (
            <Box sx={{ p: 3 }}>

                <Alert severity="error">
                    {error}
                </Alert>

                <Button
                    sx={{ mt: 2 }}
                    startIcon={<ArrowBack />}
                    onClick={() =>
                        navigate("/question-papers")
                    }
                >
                    Back
                </Button>

            </Box>
        );

    }


    if (!paper) {

        return (
            <Box sx={{ p: 3 }}>

                <Alert severity="warning">
                    Question paper not found.
                </Alert>

            </Box>
        );

    }


    // =========================================================
    // VIEW
    // =========================================================

    return (
        <Box
            className="question-paper-view-page"
            sx={{
                p: {
                    xs: 1,
                    sm: 2,
                    md: 3,
                },
            }}
        >

            {/* ================================================= */}
            {/* ACTION BAR */}
            {/* ================================================= */}

            <Stack
                className="question-paper-actions"
                direction={{
                    xs: "column",
                    sm: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                    xs: "stretch",
                    sm: "center",
                }}
                spacing={2}
                sx={{
                    mb: 2,
                }}
            >

                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() =>
                        navigate(
                            "/question-papers"
                        )
                    }
                >
                    Back
                </Button>


                <Stack
                    direction="row"
                    spacing={1}
                >

                    <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() =>
                            navigate(`/question-papers/${paper.id}/edit`)
                        }
                    >
                        Edit
                    </Button>


                    <Button
                        variant="contained"
                        startIcon={<Print />}
                        onClick={handlePrint}
                    >
                        Print
                    </Button>

                </Stack>

            </Stack>


            {/* ================================================= */}
            {/* PAPER */}
            {/* ================================================= */}

            <Card
                className="question-paper-print-area"
                sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    backgroundColor: "#fff",
                }}
            >

                <CardContent
                    className="question-paper-a4"
                >

                    {/* SCHOOL HEADER */}

                    <Box
                        className="question-paper-header"
                        sx={{
                            textAlign: "center",
                            mb: 2,
                        }}
                    >

                        <Typography
                            variant="h6"
                            fontWeight={700}
                        >
                            {schoolName}
                        </Typography>


                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ mt: 1 }}
                        >
                            {paper.examName}
                        </Typography>


                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mt: 1 }}
                        >
                            {paper.title}
                        </Typography>

                    </Box>


                    <Divider sx={{ mb: 2 }} />


                    {/* PAPER DETAILS */}

                    <Box
                        className="question-paper-details"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                            },
                            gap: 1,
                            mb: 3,
                        }}
                    >

                        <Typography>
                            <strong>Class:</strong>{" "}
                            {paper.className || "-"}
                        </Typography>


                        <Typography>
                            <strong>Subject:</strong>{" "}
                            {paper.subject || "-"}
                        </Typography>


                        <Typography>
                            <strong>Academic Year:</strong>{" "}
                            {paper.academicYear || "-"}
                        </Typography>


                        <Typography>
                            <strong>Duration:</strong>{" "}
                            {paper.duration || "-"}
                        </Typography>


                        <Typography>
                            <strong>Maximum Marks:</strong>{" "}
                            {paper.totalMarks || 0}
                        </Typography>


                        <Typography>
                            <strong>Total Questions:</strong>{" "}
                            {totalQuestions}
                        </Typography>

                    </Box>


                    {/* ================================================= */}
                    {/* SECTIONS */}
                    {/* ================================================= */}

                    {paper.sections?.map(
                        (section, sectionIndex) => (

                            <Box
                                key={
                                    section.id ||
                                    sectionIndex
                                }
                                className="question-paper-section"
                                sx={{
                                    mb: 3,
                                }}
                            >

                                {/* SECTION TITLE */}

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{
                                        mb: 1, textAlign: 'center'
                                    }}
                                >
                                    {section.name ||
                                        `Section ${sectionIndex + 1
                                        }`}
                                </Typography>


                                {/* INSTRUCTIONS */}

                                {section.instructions && (

                                    <Typography
                                        sx={{
                                            mb: 2,
                                            fontStyle:
                                                "italic",
                                        }}
                                    >
                                        {section.instructions}
                                    </Typography>

                                )}


                                {/* QUESTIONS */}

                                {section.questions?.map(
                                    (
                                        question,
                                        questionIndex
                                    ) => (

                                        <Box
                                            key={
                                                question.id ||
                                                questionIndex
                                            }
                                            className="question-paper-question"
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                mb: 2,
                                                pageBreakInside:
                                                    "avoid",
                                            }}
                                        >

                                            {/* QUESTION NUMBER */}

                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    minWidth:
                                                        "30px",
                                                }}
                                            >
                                                {questionIndex +
                                                    1}.
                                            </Typography>


                                            {/* CONTENT */}

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >

                                                <Box
                                                    className="tiptap question-content"
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            renderContent(
                                                                question.content
                                                            ),
                                                    }}
                                                />

                                            </Box>


                                            {/* MARKS */}

                                            <Typography
                                                fontWeight={600}
                                                sx={{
                                                    minWidth:
                                                        "45px",
                                                    textAlign:
                                                        "right",
                                                }}
                                            >
                                                [
                                                {question.marks ??
                                                    0}
                                                ]
                                            </Typography>

                                        </Box>

                                    )
                                )}

                            </Box>

                        )
                    )}

                </CardContent>

            </Card>

        </Box>
    );
};

export default QuestionPaperView;